import React, { useState, useEffect } from 'react';
import { useLang } from '../contexts/LanguageContext';
import { t } from '../utils/translations';
import api from '../utils/api';
import Layout from '../components/Layout';
import { StatusBadge, CategoryBadge, VisibilityBadge } from '../components/Badges';
import { Shield, AlertTriangle, Globe, Clock, ArrowUpRight, Inbox, Filter, ChevronRight, Bell, BarChart3, Layers } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

var FILTERS = [
  { key: 'all', labelFr: 'Tout', labelEn: 'All', color: '#F77F00' },
  { key: 'validate', labelFr: 'A valider', labelEn: 'To validate', color: '#A855F7' },
  { key: 'treat', labelFr: 'A traiter', labelEn: 'To handle', color: '#3B82F6' },
  { key: 'late', labelFr: 'En retard', labelEn: 'Overdue', color: '#EF4444' },
];

export default function SupportControlCenter() {
  var langCtx = useLang();
  var lang = langCtx.lang;
  var navigate = useNavigate();
  var [data, setData] = useState(null);
  var [loading, setLoading] = useState(true);
  var [activeFilter, setActiveFilter] = useState('all');

  useEffect(function() {
    api.get('/support/control-center').then(function(res) {
      setData(res.data);
    }).catch(function(err) {
      console.error(err);
    }).finally(function() {
      setLoading(false);
    });
  }, []);

  if (loading) return React.createElement(Layout, null, React.createElement('p', { className: "text-center py-12", style: { color: '#6B7280' } }, t('loading', lang)));
  if (!data) return React.createElement(Layout, null, React.createElement('p', { className: "text-center py-12", style: { color: '#EF4444' } }, 'Access denied'));

  var s = data.summary;

  // Ticket row component
  function TicketRow(props) {
    var ticket = props.ticket;
    var badge = props.badge;
    var badgeColor = props.badgeColor;
    var extra = props.extra;
    return (
      <div data-testid={'cc-ticket-' + ticket._id} onClick={function() { navigate('/tickets/' + ticket._id); }}
        className="apollo-card p-3 flex items-center gap-3 cursor-pointer transition-all mb-2"
        style={{ borderLeft: '3px solid ' + (badgeColor || '#6B7280') }}>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="font-medium text-sm text-white truncate">{lang === 'en' ? (ticket.title_en || ticket.title_fr) : ticket.title_fr}</span>
            {badge ? <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: badgeColor + '20', color: badgeColor }}>{badge}</span> : null}
            <StatusBadge status={ticket.status} />
          </div>
          <div className="flex items-center gap-3 text-xs" style={{ color: '#6B7280' }}>
            <span>{ticket.created_by_name}</span>
            <span>{ticket.category}</span>
            {extra ? <span style={{ color: badgeColor }}>{extra}</span> : null}
            <span>{new Date(ticket.created_at).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US')}</span>
          </div>
        </div>
        <ChevronRight size={14} style={{ color: '#6B7280' }} />
      </div>
    );
  }

  // Filter tickets
  var showPending = activeFilter === 'all' || activeFilter === 'validate';
  var showUnassigned = activeFilter === 'all' || activeFilter === 'treat';
  var showNoResponse = activeFilter === 'all' || activeFilter === 'treat';
  var showSLA = activeFilter === 'all' || activeFilter === 'late';
  var showEscalations = activeFilter === 'all';

  return (
    <Layout>
      <div className="animate-fade-in">
        {/* Header */}
        <div className="flex items-center gap-3 mb-5 sm:mb-6">
          <Shield size={26} style={{ color: '#F77F00', flexShrink: 0 }} />
          <div className="min-w-0">
            <h1 data-testid="control-center-title" className="text-xl sm:text-2xl font-bold text-white truncate">{lang === 'fr' ? 'Centre de Contrôle Support' : 'Support Control Center'}</h1>
            <p className="text-xs sm:text-sm truncate" style={{ color: '#9CA3AF' }}>{lang === 'fr' ? 'Vue complète de la plateforme APOLLO Ticketing' : 'Full overview of APOLLO Ticketing platform'}</p>
          </div>
        </div>

        {/* KPI Cards - 2 cols mobile, 4 cols desktop */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <div className="apollo-card p-3 sm:p-4 flex items-center gap-2 sm:gap-3 min-w-0" style={{ borderLeft: '3px solid #A855F7' }}>
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(168,85,247,0.15)' }}>
              <Globe size={18} style={{ color: '#A855F7' }} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xl sm:text-2xl font-bold text-white leading-none">{s.pending_public}</p>
              <p className="text-[10px] sm:text-xs mt-1 truncate" style={{ color: '#9CA3AF' }}>{lang === 'fr' ? 'A valider Public' : 'Pending Public'}</p>
            </div>
          </div>
          <div className="apollo-card p-3 sm:p-4 flex items-center gap-2 sm:gap-3 min-w-0" style={{ borderLeft: '3px solid #3B82F6' }}>
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(59,130,246,0.15)' }}>
              <Inbox size={18} style={{ color: '#3B82F6' }} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xl sm:text-2xl font-bold text-white leading-none">{s.unassigned + s.no_response}</p>
              <p className="text-[10px] sm:text-xs mt-1 truncate" style={{ color: '#9CA3AF' }}>{lang === 'fr' ? 'A traiter' : 'To handle'}</p>
            </div>
          </div>
          <div className="apollo-card p-3 sm:p-4 flex items-center gap-2 sm:gap-3 min-w-0" style={{ borderLeft: '3px solid #EF4444' }}>
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(239,68,68,0.15)' }}>
              <AlertTriangle size={18} style={{ color: '#EF4444' }} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xl sm:text-2xl font-bold text-white leading-none">{s.sla_critical}</p>
              <p className="text-[10px] sm:text-xs mt-1 truncate" style={{ color: '#9CA3AF' }}>{lang === 'fr' ? 'SLA critiques' : 'Critical SLA'}</p>
            </div>
          </div>
          <div className="apollo-card p-3 sm:p-4 flex items-center gap-2 sm:gap-3 min-w-0" style={{ borderLeft: '3px solid #F77F00' }}>
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(247,127,0,0.15)' }}>
              <ArrowUpRight size={18} style={{ color: '#F77F00' }} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xl sm:text-2xl font-bold text-white leading-none">{s.escalations_7d}</p>
              <p className="text-[10px] sm:text-xs mt-1 truncate" style={{ color: '#9CA3AF' }}>{lang === 'fr' ? 'Escalades (7j)' : 'Escalations (7d)'}</p>
            </div>
          </div>
        </div>

        {/* Quick Filters - scroll horizontal mobile */}
        <div className="flex gap-2 mb-6 overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 pb-1">
          {FILTERS.map(function(f) {
            var active = activeFilter === f.key;
            return (
              <button key={f.key} data-testid={'filter-' + f.key} onClick={function() { setActiveFilter(f.key); }}
                className="px-4 py-2 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 whitespace-nowrap flex-shrink-0"
                style={{ background: active ? f.color : f.color + '12', color: active ? '#fff' : f.color, border: active ? 'none' : '1px solid ' + f.color + '30', minHeight: '40px' }}>
                <Filter size={14} /> {lang === 'fr' ? f.labelFr : f.labelEn}
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* LEFT COLUMN */}
          <div className="space-y-4">
            {/* Pending Public Validation */}
            {showPending ? (
              <div data-testid="section-pending-public">
                <div className="flex items-center gap-2 mb-3">
                  <Globe size={18} style={{ color: '#A855F7' }} />
                  <h2 className="text-base font-semibold text-white">{lang === 'fr' ? 'En attente de validation publique' : 'Pending Public Validation'}</h2>
                  <span className="text-xs px-2 py-0.5 rounded-full font-bold" style={{ background: 'rgba(168,85,247,0.15)', color: '#A855F7' }}>{data.pending_public.length}</span>
                </div>
                {data.pending_public.length === 0 ? (
                  <p className="text-sm py-4 text-center apollo-card" style={{ color: '#6B7280' }}>{lang === 'fr' ? 'Aucun ticket en attente' : 'No pending tickets'}</p>
                ) : data.pending_public.map(function(t) {
                  return <TicketRow key={t._id} ticket={t} badge={lang === 'fr' ? 'A valider' : 'To validate'} badgeColor="#A855F7" />;
                })}
              </div>
            ) : null}

            {/* SLA Critical */}
            {showSLA ? (
              <div data-testid="section-sla-critical">
                <div className="flex items-center gap-2 mb-3">
                  <AlertTriangle size={18} style={{ color: '#EF4444' }} />
                  <h2 className="text-base font-semibold text-white">{lang === 'fr' ? 'SLA critiques' : 'Critical SLA'}</h2>
                  <span className="text-xs px-2 py-0.5 rounded-full font-bold" style={{ background: 'rgba(239,68,68,0.15)', color: '#EF4444' }}>{data.sla_critical.length}</span>
                </div>
                {data.sla_critical.length === 0 ? (
                  <p className="text-sm py-4 text-center apollo-card" style={{ color: '#6B7280' }}>{lang === 'fr' ? 'Aucun SLA critique' : 'No critical SLA'}</p>
                ) : data.sla_critical.map(function(t) {
                  var sla = t.sla || {};
                  var severity = t.sla_severity;
                  var extraText = severity === 'breached'
                    ? (lang === 'fr' ? 'SLA DEPASSE' : 'SLA BREACHED')
                    : (lang === 'fr' ? sla.sla_hours_remaining + 'h restantes' : sla.sla_hours_remaining + 'h left');
                  return <TicketRow key={t._id} ticket={t} badge={severity === 'breached' ? 'SLA' : (sla.sla_hours_remaining + 'h')} badgeColor={severity === 'breached' ? '#EF4444' : '#FBBF24'} extra={extraText} />;
                })}
              </div>
            ) : null}

            {/* By Category */}
            {activeFilter === 'all' ? (
              <div data-testid="section-categories">
                <div className="flex items-center gap-2 mb-3">
                  <Layers size={18} style={{ color: '#F77F00' }} />
                  <h2 className="text-base font-semibold text-white">{lang === 'fr' ? 'Par catégorie' : 'By Category'}</h2>
                </div>
                <div className="space-y-2">
                  {Object.entries(data.by_category).map(function(entry) {
                    var cat = entry[0];
                    var counts = entry[1];
                    return (
                      <div key={cat} className="apollo-card p-3 flex items-center justify-between">
                        <span className="text-sm text-white">{cat}</span>
                        <div className="flex items-center gap-3 text-xs">
                          <span style={{ color: '#F77F00' }}>{counts.total} total</span>
                          <span style={{ color: '#FBBF24' }}>{counts.open} {lang === 'fr' ? 'ouverts' : 'open'}</span>
                          <span style={{ color: '#3B82F6' }}>{counts.in_progress} {lang === 'fr' ? 'en cours' : 'in progress'}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}
          </div>

          {/* RIGHT COLUMN */}
          <div className="space-y-4">
            {/* Unassigned / No Response */}
            {showUnassigned ? (
              <div data-testid="section-unassigned">
                <div className="flex items-center gap-2 mb-3">
                  <Inbox size={18} style={{ color: '#3B82F6' }} />
                  <h2 className="text-base font-semibold text-white">{lang === 'fr' ? 'Non assignés' : 'Unassigned'}</h2>
                  <span className="text-xs px-2 py-0.5 rounded-full font-bold" style={{ background: 'rgba(59,130,246,0.15)', color: '#3B82F6' }}>{data.unassigned.length}</span>
                </div>
                {data.unassigned.length === 0 ? (
                  <p className="text-sm py-4 text-center apollo-card" style={{ color: '#6B7280' }}>{lang === 'fr' ? 'Tous les tickets sont assignés' : 'All tickets assigned'}</p>
                ) : data.unassigned.map(function(t) {
                  return <TicketRow key={t._id} ticket={t} badge={lang === 'fr' ? 'Non assigné' : 'Unassigned'} badgeColor="#3B82F6" />;
                })}
              </div>
            ) : null}

            {showNoResponse ? (
              <div data-testid="section-no-response">
                <div className="flex items-center gap-2 mb-3">
                  <Clock size={18} style={{ color: '#FBBF24' }} />
                  <h2 className="text-base font-semibold text-white">{lang === 'fr' ? 'Sans réponse (> 2 jours)' : 'No response (> 2 days)'}</h2>
                  <span className="text-xs px-2 py-0.5 rounded-full font-bold" style={{ background: 'rgba(251,191,36,0.15)', color: '#FBBF24' }}>{data.no_response.length}</span>
                </div>
                {data.no_response.length === 0 ? (
                  <p className="text-sm py-4 text-center apollo-card" style={{ color: '#6B7280' }}>{lang === 'fr' ? 'Tous les tickets ont une réponse' : 'All tickets have a response'}</p>
                ) : data.no_response.map(function(t) {
                  return <TicketRow key={t._id} ticket={t} badge={t.days_waiting + (lang === 'fr' ? 'j sans réponse' : 'd no reply')} badgeColor="#FBBF24" />;
                })}
              </div>
            ) : null}

            {/* Recent Escalations */}
            {showEscalations ? (
              <div data-testid="section-escalations">
                <div className="flex items-center gap-2 mb-3">
                  <ArrowUpRight size={18} style={{ color: '#EC4899' }} />
                  <h2 className="text-base font-semibold text-white">{lang === 'fr' ? 'Escalades récentes (7j)' : 'Recent Escalations (7d)'}</h2>
                  <span className="text-xs px-2 py-0.5 rounded-full font-bold" style={{ background: 'rgba(236,72,153,0.15)', color: '#EC4899' }}>{data.recent_escalations.length}</span>
                </div>
                {data.recent_escalations.length === 0 ? (
                  <p className="text-sm py-4 text-center apollo-card" style={{ color: '#6B7280' }}>{lang === 'fr' ? 'Aucune escalade récente' : 'No recent escalations'}</p>
                ) : data.recent_escalations.map(function(esc, idx) {
                  return (
                    <div key={idx} onClick={function() { navigate('/tickets/' + esc.ticket_id); }}
                      className="apollo-card p-3 mb-2 cursor-pointer" style={{ borderLeft: '3px solid #EC4899' }}>
                      <p className="text-sm font-medium text-white mb-1">{lang === 'en' ? (esc.title_en || esc.title_fr) : esc.title_fr}</p>
                      <div className="flex items-center gap-2 text-xs" style={{ color: '#9CA3AF' }}>
                        <span>{esc.from_name} ({esc.from_role})</span>
                        <ArrowUpRight size={10} style={{ color: '#EC4899' }} />
                        <span className="text-white">{esc.to_name}</span>
                        <span>{new Date(esc.timestamp).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US')}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </Layout>
  );
}
